/**
 * Chat List Sidebar
 * Displays list of all user's chats with filtering by type
 */

import React, { useMemo, useState } from 'react';
import { List, Typography, Spin, Empty, Badge, Segmented } from 'antd';
import {
  MessageOutlined,
  TeamOutlined,
  FileTextOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import { useChats } from '@/hooks/useChat';
import { ChatType, type Chat } from '@/api/types/chat.types';
import './ChatListSidebar.css';

const { Text } = Typography;

interface ChatListSidebarProps {
  selectedChatId?: string;
  onChatSelect: (chat: Chat) => void;
}

type FilterType = 'all' | 'direct' | 'groups' | 'notes';

export const ChatListSidebar: React.FC<ChatListSidebarProps> = ({
  selectedChatId,
  onChatSelect,
}) => {
  const { data: chats, isLoading } = useChats();
  const [filter, setFilter] = useState<FilterType>('all');

  // Filter chats by type
  const filteredChats = useMemo(() => {
    if (!chats) return [];

    switch (filter) {
      case 'direct':
        return chats.filter((chat) => chat.chat_type === ChatType.ONE_TO_ONE);
      case 'groups':
        return chats.filter((chat) => chat.chat_type === ChatType.GROUP);
      case 'notes':
        return chats.filter((chat) => chat.chat_type === ChatType.MY_NOTES);
      case 'all':
      default:
        return chats;
    }
  }, [chats, filter]);

  // Get icon for chat type
  const getChatIcon = (chat: Chat) => {
    if (chat.is_general) {
      return <GlobalOutlined className="chat-icon chat-icon-general" />;
    }

    switch (chat.chat_type) {
      case ChatType.ONE_TO_ONE:
        return <MessageOutlined className="chat-icon chat-icon-direct" />;
      case ChatType.GROUP:
        return <TeamOutlined className="chat-icon chat-icon-group" />;
      case ChatType.MY_NOTES:
        return <FileTextOutlined className="chat-icon chat-icon-notes" />;
      default:
        return <MessageOutlined className="chat-icon" />;
    }
  };

  // Get badge count for unread messages (placeholder - will be implemented with WebSocket)
  const getUnreadCount = (_chat: Chat): number => {
    // TODO: Implement unread count from WebSocket events
    return 0;
  };

  if (isLoading) {
    return (
      <div className="chat-list-sidebar">
        <div className="chat-list-loading">
          <Spin tip="Loading chats..." />
        </div>
      </div>
    );
  }

  return (
    <div className="chat-list-sidebar">
      <div className="chat-list-header">
        <Text strong style={{ fontSize: 16 }}>
          Chats
        </Text>
      </div>

      <div className="chat-list-filters">
        <Segmented
          value={filter}
          onChange={(value) => setFilter(value as FilterType)}
          options={[
            { label: 'All', value: 'all' },
            { label: 'Direct', value: 'direct' },
            { label: 'Groups', value: 'groups' },
            { label: 'Notes', value: 'notes' },
          ]}
          block
        />
      </div>

      <div className="chat-list-content">
        {filteredChats.length === 0 ? (
          <Empty
            description="No chats found"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <List
            dataSource={filteredChats}
            renderItem={(chat) => {
              const unreadCount = getUnreadCount(chat);
              const isSelected = chat.id === selectedChatId;

              return (
                <List.Item
                  className={`chat-list-item ${isSelected ? 'chat-list-item-selected' : ''}`}
                  onClick={() => onChatSelect(chat)}
                >
                  <div className="chat-list-item-content">
                    <div className="chat-list-item-icon">
                      {getChatIcon(chat)}
                    </div>

                    <div className="chat-list-item-info">
                      <div className="chat-list-item-header">
                        <Text strong className="chat-list-item-title">
                          {chat.title}
                        </Text>
                        {unreadCount > 0 && (
                          <Badge
                            count={unreadCount}
                            size="small"
                            className="chat-list-item-badge"
                          />
                        )}
                      </div>

                      {chat.description && (
                        <Text
                          type="secondary"
                          className="chat-list-item-description"
                        >
                          {chat.description}
                        </Text>
                      )}

                      {chat.participant_count !== undefined && (
                        <Text
                          type="secondary"
                          className="chat-list-item-participants"
                        >
                          {chat.participant_count}{' '}
                          {chat.participant_count === 1
                            ? 'participant'
                            : 'participants'}
                        </Text>
                      )}
                    </div>
                  </div>
                </List.Item>
              );
            }}
          />
        )}
      </div>
    </div>
  );
};
